import { formatInt, formatNumber } from "../../../shared/utils/numberLocale";

export function unwrapSettingsRows(settingOrJson) {
  const json =
    settingOrJson && typeof settingOrJson === "object" && "settings_json" in settingOrJson
      ? settingOrJson.settings_json
      : settingOrJson;

  if (Array.isArray(json)) return json;
  if (json && typeof json === "object") {
    if (Array.isArray(json.data)) return json.data;
    if (Array.isArray(json.products)) return json.products;
  }
  return [];
}

export function parseIdArray(value) {
  if (Array.isArray(value)) {
    return value.map(Number).filter(Number.isFinite);
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map(Number).filter(Number.isFinite);
      }
    } catch {
      /* fall through to split */
    }
    return trimmed
      .split(/[^\d-]+/)
      .map(Number)
      .filter(Number.isFinite);
  }
  return [];
}

export function inferRowLabel(row, valueField, optionsLabel, optionsCode) {
  if (!row || typeof row !== "object") return String(row ?? "");
  const name =
    (optionsLabel && row[optionsLabel]) ||
    row.title ||
    row.name ||
    row.manufacturer_name ||
    row.GoodsGroupName ||
    row.goodsgroup_name ||
    row.product_group_name ||
    row.saleoffice_name;
  const code = (optionsCode && row[optionsCode]) || row.manufacturer_code || row.code;
  const id = valueField ? row[valueField] : row.id ?? row.ID;
  if (name && code) return `${name} (${code})`;
  if (name && id != null && id !== "") return `${name} (${id})`;
  return name || String(id ?? "");
}

export function getByPath(source, path) {
  if (!path) return source;
  return String(path)
    .split(".")
    .reduce((acc, key) => (acc == null ? undefined : acc[key]), source);
}

export function setByPath(source, path, value) {
  if (!path) return value;

  const keys = String(path).split(".");
  const root = Array.isArray(source)
    ? [...source]
    : { ...(source && typeof source === "object" ? source : {}) };

  let cursor = root;
  for (let i = 0; i < keys.length - 1; i += 1) {
    const key = keys[i];
    const next = cursor[key];
    cursor[key] = Array.isArray(next)
      ? [...next]
      : { ...(next && typeof next === "object" ? next : {}) };
    cursor = cursor[key];
  }

  cursor[keys[keys.length - 1]] = value;
  return root;
}

export function getFieldLabel(field) {
  return field?.fa || field?.en || field?.key || "";
}

export function getSchemaFields(schema) {
  if (!schema) return [];
  if (Array.isArray(schema.fields)) return schema.fields;
  if (schema.fields && typeof schema.fields === "object") {
    return Object.entries(schema.fields).map(([key, field]) => ({
      key,
      ...field,
    }));
  }
  return [];
}

export function buildEmptyItem(fields = []) {
  return fields.reduce((acc, field) => {
    if (field.default !== undefined) {
      acc[field.key] = field.default;
      return acc;
    }

    switch (field.type) {
      case "number":
        acc[field.key] = "";
        break;
      case "boolean":
        acc[field.key] = false;
        break;
      case "multiselect":
      case "tags":
      case "array":
        acc[field.key] = [];
        break;
      default:
        acc[field.key] = "";
    }
    return acc;
  }, {});
}

export function normalizeFieldValue(field, rawValue) {
  switch (field.type) {
    case "number": {
      if (rawValue === "" || rawValue === null || rawValue === undefined) {
        return field.required ? null : null;
      }
      const num = Number(rawValue);
      return Number.isNaN(num) ? null : num;
    }
    case "boolean":
      return Boolean(rawValue);
    case "multiselect":
    case "tags":
    case "array":
      if (Array.isArray(rawValue)) return rawValue;
      if (typeof rawValue === "string") {
        return rawValue
          .split(/[,\n]/)
          .map((item) => item.trim())
          .filter(Boolean);
      }
      return [];
    default:
      return rawValue ?? "";
  }
}

export function readFieldValue(data, field) {
  const value = field.path ? getByPath(data, field.path) : data?.[field.key];
  if (value === undefined || value === null) {
    return field.default !== undefined
      ? field.default
      : field.type === "boolean"
        ? false
        : field.type === "multiselect" || field.type === "tags" || field.type === "array"
          ? []
          : "";
  }
  return value;
}

export function writeFieldValue(data, field, value) {
  const normalized = normalizeFieldValue(field, value);
  if (field.path) {
    return setByPath(data, field.path, normalized);
  }
  return {
    ...(data && typeof data === "object" ? data : {}),
    [field.key]: normalized,
  };
}

export function resolveOptions(field, allSettings = [], context = {}) {
  if (Array.isArray(field.options) && field.options.length > 0) {
    return field.options.map((option) =>
      typeof option === "object"
        ? {
            value: option.value,
            label: option.label ?? String(option.value),
          }
        : { value: option, label: String(option) },
    );
  }

  if (!field.options_from || typeof field.options_from !== "string") {
    return [];
  }

  if (field.options_from === "formulas") {
    const extras = Array.isArray(field.extra_options) ? field.extra_options : [];
    const mapped = [];
    (allSettings || []).forEach((setting) => {
      const type = String(setting?.settings_type || "").toLowerCase();
      if (type !== "formula") return;
      const json = setting.settings_json || {};
      const map =
        json.formulas && typeof json.formulas === "object" && !Array.isArray(json.formulas)
          ? json.formulas
          : {};
      const add = (key, def) => {
        if (!key) return;
        mapped.push({
          value: key,
          label: `${def?.title || def?.fa_description || key} — ${setting.title || setting.settings_key}`,
        });
      };
      Object.entries(map).forEach(([key, def]) => add(key, def));
      if (json.suggestion_formula) add("suggestion_formula", json.suggestion_formula);
    });
    const seen = new Set(extras.map((o) => String(o.value ?? o)));
    return [
      ...extras.map((option) =>
        option && typeof option === "object"
          ? { value: option.value, label: option.label ?? String(option.value) }
          : { value: option, label: String(option) },
      ),
      ...mapped.filter((option) => !seen.has(String(option.value))),
    ];
  }

  // Format: settings:<settings_key>.<itemField>
  const match = field.options_from.match(/^settings:([^.]+)\.(.+)$/);
  if (!match) return [];

  const [, settingsKey, itemField] = match;
  const setting = allSettings.find((row) => row.settings_key === settingsKey);
  const rows = unwrapSettingsRows(setting);

  const extras = Array.isArray(field.extra_options)
    ? field.extra_options
        .map((option) =>
          option && typeof option === "object"
            ? {
                value: option.value,
                label: option.label ?? String(option.value),
              }
            : { value: option, label: String(option) },
        )
        .filter((option) => option.value !== undefined && option.value !== null && option.value !== "")
    : [];

  const mapped = rows
    .map((row) => {
      const value = row?.[itemField];
      if (value === undefined || value === null || value === "") return null;
      const label = inferRowLabel(row, itemField, field.options_label, field.options_code);
      return { value, label, row };
    })
    .filter(Boolean);

  const seen = new Set(extras.map((option) => String(option.value)));
  return [
    ...extras,
    ...mapped.filter((option) => !seen.has(String(option.value))),
  ];
}

export function validateFields(fields, values) {
  const errors = {};

  fields.forEach((field) => {
    const value = values?.[field.key];
    const emptyArray = Array.isArray(value) && value.length === 0;
    const emptyText = value === "" || value === null || value === undefined;

    if (field.required && (emptyText || emptyArray)) {
      errors[field.key] = `${getFieldLabel(field)} الزامی است`;
    }

    if (field.type === "number" && value !== "" && value != null) {
      const num = Number(value);
      if (Number.isNaN(num)) {
        errors[field.key] = `${getFieldLabel(field)} باید عدد باشد`;
      } else {
        if (field.min != null && num < field.min) {
          errors[field.key] = `${getFieldLabel(field)} نباید کمتر از ${formatNumber(field.min)} باشد`;
        }
        if (field.max != null && num > field.max) {
          errors[field.key] = `${getFieldLabel(field)} نباید بیشتر از ${formatNumber(field.max)} باشد`;
        }
      }
    }
  });

  return errors;
}

export function formatCellValue(value) {
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "boolean") return value ? "بله" : "خیر";
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "number" && Number.isFinite(value)) {
    return Number.isInteger(value) ? formatInt(value) : formatNumber(value, { maximumFractionDigits: 4 });
  }
  if (typeof value === "string" && value.trim() !== "" && /^-?\d+(\.\d+)?$/.test(value.trim())) {
    const n = Number(value);
    return Number.isInteger(n) ? formatInt(n) : formatNumber(n, { maximumFractionDigits: 4 });
  }
  return String(value);
}
